import React, { useCallback, useEffect, useState } from 'react';
import { api, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, UserCog, Trash2, Save, KeyRound, Copy, Check, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { normalizeRole, roleLabel } from '../lib/roles';
import { loginDe } from '../lib/login';
import { useNavigate } from 'react-router';

// LOS SEIS ROLES, no tres (2026-09-21: «al entrar en usuarios no me
// deja asignarle rol de doctor»). El servidor acepta desde hace tiempo los
// seis —models.USER_ROLE: admin, direccion, administrativo, doctor,
// enfermeria, anestesista— pero esta lista se quedó con los tres de
// administración. No es que la pantalla lo rechazara: es que no estaba, así
// que a una doctora nueva no había forma de ponerle su rol desde aquí y había
// que tocarlo por detrás. Los nombres salen de lib/roles.ROLES; aquí se les
// añade en una línea QUÉ VE cada uno, que es lo que se decide al elegir.
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin · acceso total y gestión de usuarios' },
  { value: 'direccion', label: 'Dirección · acceso completo (cobros, KPIs, exports)' },
  { value: 'administrativo', label: 'Administrativo · operativa diaria sin visión global' },
  { value: 'doctor', label: 'Doctor · su agenda, sus pacientes y su historia clínica' },
  { value: 'enfermeria', label: 'Enfermería · quirófano y consulta, sin cobros ni KPIs' },
  { value: 'anestesista', label: 'Anestesista · solo Quirófano y Anestesia' },
];

/** Contraseña provisional aleatoria (el usuario la cambia en el primer login).
 *  Sin caracteres ambiguos (0/O, 1/l/I). No usamos una fija tipo "123456". */
function randomPwd(n = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  if (window.crypto?.getRandomValues) {
    const arr = new Uint32Array(n);
    window.crypto.getRandomValues(arr);
    for (let i = 0; i < n; i++) out += chars[arr[i] % chars.length];
  } else {
    for (let i = 0; i < n; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function UserDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const initialRole = initial ? normalizeRole(initial.role) : 'administrativo';
  const [form, setForm] = useState(initial ? { ...initial, role: initialRole } : { email: '', name: '', role: 'administrativo', password: randomPwd() });

  useEffect(() => {
    if (open) {
      const i = initial
        ? { ...initial, role: normalizeRole(initial.role) }
        : { email: '', name: '', role: 'administrativo', password: randomPwd() };
      setForm(i);
    }
  }, [open, initial]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        // Sin `puede_borrar_episodios` (D4, 22/09/2026): borrar
        // episodios es del rol admin y la marca por persona ya no cuenta.
        const payload = { name: form.name, role: form.role, apodo: (form.apodo || '').trim(),
          agente_notas: (form.agente_notas || '').trim() };
        if (form.password) payload.password = form.password;
        if ('active' in form) payload.active = form.active;
        await api.put(`/api/users/${initial.id}`, payload);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/api/users', form);
        toast.success(`Usuario creado · contraseña inicial: ${form.password}`);
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          {!isEdit && (
            <DialogDescription className="text-xs">
              Se genera una <strong>contraseña provisional aleatoria</strong> (puedes cambiarla).
              El usuario deberá <strong>cambiarla en el primer login</strong>.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input required type="email" disabled={isEdit} value={form.email} onChange={(e) => setF('email', e.target.value)} data-testid="user-email" />
          </div>
          <div>
            <Label>Nombre</Label>
            <Input required value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="user-name" />
          </div>
          <div>
            <Label>Apodo (cómo le llama el Agente)</Label>
            <Input value={form.apodo || ''} onChange={(e) => setF('apodo', e.target.value)}
              placeholder="Ej.: «dirección», «administración», «enfermería», «el doctor»… vacío = su nombre" maxLength={40}
              data-testid="user-apodo" />
          </div>
          <div>
            <Label>Cómo debe tratarle el Agente</Label>
            <textarea value={form.agente_notas || ''} onChange={(e) => setF('agente_notas', e.target.value)}
              rows={3} maxLength={600}
              placeholder="Ej.: prefiere explicaciones paso a paso y sin tecnicismos"
              className="mt-1.5 w-full rounded-md border border-input bg-white p-2 text-sm"
              data-testid="user-agente-notas" />
          </div>
          <div>
            <Label>Rol</Label>
            <Select value={form.role} onValueChange={(v) => setF('role', v)}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Aquí estuvo la casilla «Puede borrar episodios» (permiso por
              persona, 22/09/2026). Se quitó con la decisión D4 de dirección ese
              mismo día: «episodios solo puede borrar administradores, igual
              que gestionar usuarios o crear usuarios». Lo da el rol Admin de
              arriba; una casilla que ya no cambia nada sería una mentira. */}

          <div>
            <Label>{isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña inicial'}</Label>
            <Input
              type="text"
              value={form.password || ''}
              onChange={(e) => setF('password', e.target.value)}
              placeholder={isEdit ? 'Dejar en blanco para no cambiar' : 'Contraseña provisional'}
              data-testid="user-password"
            />
            {isEdit && form.password && (
              <p className="text-xs text-amber-700 mt-1">Al cambiar la contraseña, el usuario deberá actualizarla en su próximo login.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-save">
              <Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, target, onDone }) {
  const [newPwd, setNewPwd] = useState(randomPwd);
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) { setNewPwd(randomPwd()); setShowResult(false); setCopied(false); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/users/${target.id}/reset-password`, { new_password: newPwd });
      setShowResult(true);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error reseteando');
    }
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(newPwd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resetear contraseña — {target.name}</DialogTitle>
          <DialogDescription className="text-xs">
            El usuario tendrá que <strong>cambiar la contraseña en su próximo login</strong>.
            Comparte la contraseña provisional sólo por un canal seguro.
          </DialogDescription>
        </DialogHeader>
        {!showResult ? (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nueva contraseña provisional</Label>
              <Input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                minLength={8}
                required
                data-testid="user-reset-pwd-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Mínimo 8 caracteres.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-reset-pwd-confirm">
                <KeyRound className="w-4 h-4 mr-2" /> Resetear
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-300 rounded-md p-3">
              <div className="font-semibold text-green-900 mb-1">Contraseña reseteada</div>
              <p className="text-xs text-green-900">Comparte esta contraseña con <strong>{target.name}</strong>. Al iniciar sesión, se le pedirá cambiarla.</p>
            </div>
            <div className="bg-muted/40 border border-border rounded-md p-3 flex items-center justify-between">
              <code className="text-sm font-mono">{newPwd}</code>
              <Button size="sm" variant="outline" onClick={copyPwd} data-testid="user-reset-pwd-copy">
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copiado</> : <><Copy className="w-4 h-4 mr-1" /> Copiar</>}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => { if (user && user.role !== 'admin') nav('/'); }, [user, nav]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/users');
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const onDelete = async (it) => {
    if (!window.confirm(`¿Desactivar a ${it.name}? El usuario ya no podrá iniciar sesión.`)) return;
    try {
      await api.delete(`/api/users/${it.id}`);
      toast.success('Usuario desactivado');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const openReset = (it) => {
    setResetTarget(it);
    setResetOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        subtitle={`${items.length} usuarios del equipo · gestión solo para administradores`}
        actions={(
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-new">
            <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
          </Button>
        )}
      />
      <div className="cp-card overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={UserCog} title="Sin usuarios" description="Añade el primero." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Email</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Rol</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Estado</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Alta</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.filter(u => u.active !== false).map(u => (
                  <tr key={u.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="user-row">
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#0B1220]">{u.name}</div>
                      <div className="text-[10px] text-muted-foreground" data-testid="user-login">
                        login: <code className="bg-muted/40 px-1 rounded">{loginDe(u)}</code>
                        {/* El correo vale SIEMPRE, se tenga usuario corto o no
                            (21/9: al ponerles usuario a todos, este aviso se
                            habría apagado para toda la pantalla). */}
                        {' · o su correo'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-4 text-xs uppercase tracking-wider">{roleLabel(u.role)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {u.active === false ? (
                          <Badge variant="secondary" className="bg-red-50 text-red-800 border-red-300">Desactivado</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-50 text-green-800 border-green-300">Activo</Badge>
                        )}
                        {u.must_change_password && (
                          <Badge variant="secondary" className="bg-amber-50 text-amber-900 border-amber-300" title="Debe cambiar la contraseña en el próximo login">
                            <KeyRound className="w-3 h-3 mr-1" /> Cambio pendiente
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs">{formatDate(u.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(u); setOpen(true); }} title="Editar" data-testid="user-edit">
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openReset(u)} title="Resetear contraseña" data-testid="user-reset">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        {u.id !== user?.id && u.active !== false && (
                          <Button size="sm" variant="ghost" onClick={() => onDelete(u)} className="text-destructive" title="Desactivar" data-testid="user-delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <UserDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} />
      <ResetPasswordDialog open={resetOpen} onOpenChange={setResetOpen} target={resetTarget} onDone={fetchItems} />
    </div>
  );
}
